import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, ChevronDown } from "lucide-react";
import useSiteConfig, { whatsappLinkFromCfg } from "@/lib/useSiteConfig";

/**
 * Site header.
 * - Desktop: Collections + Locations open hover/click dropdowns so users can pick
 *   the exact sub-category instead of landing on a default one.
 * - Mobile: accordion-style sub-sections inside the drawer.
 */

const COLLECTIONS_MENU = [
  { slug: "couples", label: "Couples Escapes" },
  { slug: "cinematic", label: "Cinematic Stays" },
  { slug: "experiential-design", label: "Experiential Getaways" },
  { slug: "private-gatherings", label: "Private Gatherings" },
  { slug: "long-stays", label: "Long Stays & Workations" },
];

const LOCATIONS_MENU = [
  { slug: "south-delhi", label: "Near Dhan Mill · South Delhi" },
  { slug: "gk1", label: "Greater Kailash · South Delhi" },
  { slug: "gurugram", label: "Uppal Southend · Gurugram" },
];

const SIMPLE_NAV = [
  { to: "/stays", label: "Stays" },
  { to: "/guest-love", label: "Guest Love" },
  { to: "/about", label: "About" },
  { to: "/shoots", label: "Creators & Shoots" },
  { to: "/contact", label: "Contact" },
];

function Dropdown({ label, items, pathPrefix, testId }) {
  const [open, setOpen] = useState(false);
  const [sticky, setSticky] = useState(false); // Click-opened: don't auto-close on mouseleave
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const location = useLocation();
  const isActive = location.pathname.startsWith(pathPrefix);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSticky(false); }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    setOpen(false); setSticky(false);
  }, [location.pathname]);

  // Clicking the trigger opens the menu "persistently" — it stays open until the user
  // clicks outside, picks an item, or navigates. Hover opens on desktops too (with a
  // small idle close delay so travelling the cursor from button to menu doesn't flicker).
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { if (sticky) return; cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 180); };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <button
        onClick={() => {
          cancelClose();
          // If the menu is already open (because the user hovered in), clicking
          // should STICK it open rather than toggle it closed.
          if (open && !sticky) { setSticky(true); return; }
          const next = !open;
          setOpen(next);
          setSticky(next);
        }}
        data-testid={testId}
        aria-expanded={open}
        aria-haspopup="true"
        className={`inline-flex items-center gap-1 font-editorial text-base transition-colors ${
          isActive ? "text-forest" : "text-noir/70 hover:text-forest"
        }`}
      >
        {label} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid={`${testId}-menu`}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-2"
        >
          <div className="w-64 overflow-hidden rounded-2xl border border-forest/15 bg-parchment shadow-soft">
            <ul className="flex flex-col py-2">
              {items.map((i) => (
                <li key={i.slug}>
                  <Link
                    to={`${pathPrefix}/${i.slug}`}
                    onClick={() => { setOpen(false); setSticky(false); }}
                    className="block px-5 py-2.5 text-sm text-noir/80 transition-colors hover:bg-mist/70 hover:text-forest"
                    data-testid={`${testId}-item-${i.slug}`}
                  >
                    {i.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const cfg = useSiteConfig();
  const waLink = whatsappLinkFromCfg(cfg);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-forest/10 bg-parchment/80 backdrop-blur-md"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src="/vyagram-logo.png" alt="Vyagram Experiences" className="h-9 w-auto sm:h-11" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          <NavLink
            to="/stays"
            data-testid="nav-stays"
            className={({ isActive }) =>
              `font-editorial text-base transition-colors ${isActive ? "text-forest" : "text-noir/70 hover:text-forest"}`
            }
          >
            Stays
          </NavLink>
          <Dropdown label="Collections" items={COLLECTIONS_MENU} pathPrefix="/collections" testId="nav-collections" />
          <Dropdown label="Locations" items={LOCATIONS_MENU} pathPrefix="/locations" testId="nav-locations" />
          {SIMPLE_NAV.slice(1).map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              data-testid={`nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-editorial text-base transition-colors ${
                  isActive ? "text-forest" : "text-noir/70 hover:text-forest"
                }`
              }
            >
              {i.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="hidden items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-medium text-white transition-transform hover:-translate-y-0.5 lg:inline-flex"
            data-testid="header-whatsapp"
          >
            <MessageCircle size={15} /> Talk to us
          </a>
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-[#25D366] p-2 text-white lg:hidden"
            data-testid="header-whatsapp-compact"
            aria-label="WhatsApp"
          >
            <MessageCircle size={16} />
          </a>
          <div className="hidden lg:block">
            <Link to="/stays" className="vyg-cta" data-testid="header-book-cta">
              Check Availability
            </Link>
          </div>
          <Link
            to="/stays"
            className="inline-flex items-center justify-center rounded-full border border-forest/30 bg-forest px-3.5 py-2 text-xs font-medium text-parchment sm:px-4 sm:text-sm lg:hidden"
            data-testid="header-book-cta-compact"
          >
            Book
          </Link>
          <button
            className="rounded-full border border-forest/20 p-2 lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-forest/10 bg-parchment" data-testid="mobile-menu">
          <div className="flex flex-col px-4 py-2">
            <Link to="/stays" onClick={() => setOpen(false)} className="font-editorial py-3 text-lg text-noir" data-testid="mobile-nav-stays">Stays</Link>

            <MobileAccordion
              label="Collections"
              testId="mobile-nav-collections"
              active={mobileSection === "collections"}
              onToggle={() => setMobileSection((s) => (s === "collections" ? null : "collections"))}
            >
              {COLLECTIONS_MENU.map((i) => (
                <Link
                  key={i.slug}
                  to={`/collections/${i.slug}`}
                  onClick={() => setOpen(false)}
                  className="block py-2 pl-4 text-sm text-noir/80"
                  data-testid={`mobile-nav-collections-item-${i.slug}`}
                >
                  {i.label}
                </Link>
              ))}
            </MobileAccordion>

            <MobileAccordion
              label="Locations"
              testId="mobile-nav-locations"
              active={mobileSection === "locations"}
              onToggle={() => setMobileSection((s) => (s === "locations" ? null : "locations"))}
            >
              {LOCATIONS_MENU.map((i) => (
                <Link
                  key={i.slug}
                  to={`/locations/${i.slug}`}
                  onClick={() => setOpen(false)}
                  className="block py-2 pl-4 text-sm text-noir/80"
                  data-testid={`mobile-nav-locations-item-${i.slug}`}
                >
                  {i.label}
                </Link>
              ))}
            </MobileAccordion>

            {SIMPLE_NAV.slice(1).map((i) => (
              <Link
                key={i.to}
                to={i.to}
                onClick={() => setOpen(false)}
                className="font-editorial py-3 text-lg text-noir"
                data-testid={`mobile-nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {i.label}
              </Link>
            ))}

            <a href={waLink} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 self-start rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-medium text-white">
              <MessageCircle size={15} /> Talk to us
            </a>
            <Link to="/stays" className="vyg-cta mt-3 mb-4 self-start" onClick={() => setOpen(false)} data-testid="mobile-book-cta">
              Check Availability
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileAccordion({ label, testId, active, onToggle, children }) {
  return (
    <div className="border-b border-forest/5 last:border-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between py-3 font-editorial text-lg text-noir"
        data-testid={testId}
        aria-expanded={active}
      >
        {label}
        <ChevronDown size={16} className={`transition-transform ${active ? "rotate-180" : ""}`} />
      </button>
      {active && <div className="pb-2">{children}</div>}
    </div>
  );
}
